import React, { useState, useEffect, useCallback, useRef } from "react";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MultiSelect } from "@/components/MultiSelect/MultiSelect";
import type { TransactionHistoryReportFilters } from "../../hooks/useTransactionHistoryReport";

interface TransactionHistoryReportFiltersProps {
  filters: TransactionHistoryReportFilters;
  isExporting: boolean;
  onFilterChange: (
    key: keyof TransactionHistoryReportFilters,
    value: string | string[] | undefined,
  ) => void;
  onExportCsv: () => void;
}

const TYPE_OPTIONS = [
  { value: "payment", label: "Payment" },
  { value: "refund", label: "Refund" },
  { value: "voucher_redemption", label: "Voucher Redemption" },
];

const STATUS_OPTIONS = [
  { value: "completed", label: "Completed" },
  { value: "expired", label: "Expired" },
  { value: "canceled", label: "Canceled" },
  { value: "refunded", label: "Refunded" },
  { value: "recognized", label: "Recognized" },
];

const TransactionHistoryReportFilters: React.FC<
  TransactionHistoryReportFiltersProps
> = ({ filters, isExporting, onFilterChange, onExportCsv }) => {
  const [searchTerm, setSearchTerm] = useState(filters.searchTerm ?? "");
  const [types, setTypes] = useState<string[]>(filters.types ?? []);
  const [statuses, setStatuses] = useState<string[]>(filters.statuses ?? []);
  const [dateFrom, setDateFrom] = useState(filters.dateFrom ?? "");
  const [dateTo, setDateTo] = useState(filters.dateTo ?? "");

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500);
  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    onFilterChange("searchTerm", debouncedSearchTerm || undefined);
  }, [debouncedSearchTerm, onFilterChange]);

  const handleTypeChange = (value: string[]) => {
    setTypes(value);
    onFilterChange("types", value.length ? value : undefined);
  };

  const handleStatusChange = (value: string[]) => {
    setStatuses(value);
    onFilterChange("statuses", value.length ? value : undefined);
  };

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateFrom(e.target.value);
    onFilterChange("dateFrom", e.target.value || undefined);
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateTo(e.target.value);
    onFilterChange("dateTo", e.target.value || undefined);
  };

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setTypes([]);
    setStatuses([]);
    setDateFrom("");
    setDateTo("");
    onFilterChange("searchTerm", undefined);
    onFilterChange("types", undefined);
    onFilterChange("statuses", undefined);
    onFilterChange("dateFrom", undefined);
    onFilterChange("dateTo", undefined);
  }, [onFilterChange]);

  const hasActiveFilters =
    searchTerm || types.length || statuses.length || dateFrom || dateTo;

  return (
    <div className="mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <div>
          <Label htmlFor="searchTerm">Search</Label>
          <Input
            className="mt-1.5 bg-white"
            id="searchTerm"
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Confirmation #, company..."
            type="text"
            value={searchTerm}
          />
        </div>

        <div>
          <Label>Type</Label>
          <MultiSelect
            className="mt-1.5"
            onChange={handleTypeChange}
            options={TYPE_OPTIONS}
            placeholder="All Types"
            value={types}
          />
        </div>

        <div>
          <Label>Status</Label>
          <MultiSelect
            className="mt-1.5"
            onChange={handleStatusChange}
            options={STATUS_OPTIONS}
            placeholder="All Statuses"
            value={statuses}
          />
        </div>

        <div>
          <Label htmlFor="dateFrom">Date From</Label>
          <Input
            className="mt-1.5 bg-white"
            id="dateFrom"
            onChange={handleDateFromChange}
            type="date"
            value={dateFrom}
          />
        </div>

        <div>
          <Label htmlFor="dateTo">Date To</Label>
          <Input
            className="mt-1.5 bg-white"
            id="dateTo"
            onChange={handleDateToChange}
            type="date"
            value={dateTo}
          />
        </div>

        <div className="flex items-end gap-2">
          <Button
            className="border-gray-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            disabled={!hasActiveFilters}
            onClick={handleClearFilters}
            variant="outline"
          >
            Clear Filters
          </Button>
          <Button
            className="bg-green-600 hover:bg-green-700"
            disabled={isExporting}
            onClick={onExportCsv}
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            {isExporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TransactionHistoryReportFilters;
